/**
 * API 클라이언트 - 프록시 서버를 통한 FLUX API 호출
 */

import axios from 'axios';

// Access Code는 환경 변수 또는 로컬 스토리지에서 가져오기
const getAccessCode = () => {
  return localStorage.getItem('access_code') || process.env.REACT_APP_ACCESS_CODE || 'guest';
};

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL || 'https://myai.doldari.com:8443',
  timeout: process.env.REACT_APP_TIMEOUT, // 3분
  headers: {
    'Content-Type': 'application/json'
  }
});

// 요청 인터셉터 - 모든 요청에 Authorization 헤더 추가
apiClient.interceptors.request.use(
  (config) => {
    const accessCode = getAccessCode();
    if (accessCode) {
      config.headers['Authorization'] = `Bearer ${accessCode}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('인증 실패: Access Code가 유효하지 않습니다.');
      // Access Code 재입력 요청 또는 로그인 페이지로 이동
    }
    return Promise.reject(error);
  }
);

// API 함수들
export const fluxAPI = {
  /**
   * FLUX 서버 상태 확인
   */
  checkHealth: async () => {
    const response = await apiClient.get('/flux/health');
    return response.data;
  },

  /**
   * 이미지 생성 (JSON 방식)
   */
  generateImage: async (params) => {
    const response = await apiClient.post('/flux/generate', params);
    return response.data;
  },

  /**
   * 이미지 생성 (참조 이미지 포함)
   */
  generateImageWithReferences: async (params, files) => {
    const formData = new FormData();

    // 파라미터 추가
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        formData.append(key, params[key].toString());
      }
    });

    // 파일 추가
    if (files && files.length > 0) {
      files.forEach((file, index) => {
        formData.append(`image_${index + 1}`, file);
      });
    }

    const response = await apiClient.post('/flux/generate-with-images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * 이미지 다운로드 URL 생성
   */
  getDownloadUrl: (filename) => {
    // URL에 access_code를 포함하지 않음 (Authorization 헤더 사용)
    return `${process.env.REACT_APP_PROXY_URL || 'http://localhost:8081'}/flux/download/${filename}`;
  },

  /**
   * 이미지를 Blob으로 다운로드 (미리보기용)
   */
  downloadImageAsBlob: async (filename) => {
    try {
      const response = await apiClient.get(`/flux/download/${filename}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error(`이미지 다운로드 실패: ${filename}`, error);
      throw error;
    }
  },

  /**
   * 이미지 파일 다운로드 (파일 저장용)
   */
  downloadImage: async (filename) => {
    try {
      const response = await apiClient.get(`/flux/download/${filename}`, {
        responseType: 'blob'
      });
      const blob = response.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Blob URL 정리
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error(`다운로드 실패: ${filename}`, error);
      throw error;
    }
  },

  /**
   * 이미지 목록 조회
   */
  listImages: async () => {
    const response = await apiClient.get('/flux/list');
    return response.data;
  },

  /**
   * Access Code 검증
   */
  verifyAccessCode: async (code) => {
    const response = await apiClient.post('/verify-code', { code });
    return response.data;
  }
};

// Access Code 관리 함수
export const accessCodeManager = {
  /**
   * Access Code 저장
   */
  setAccessCode: (code) => {
    localStorage.setItem('access_code', code);
  },

  /**
   * Access Code 가져오기
   */
  getAccessCode: () => {
    return getAccessCode();
  },

  /**
   * Access Code 삭제
   */
  clearAccessCode: () => {
    localStorage.removeItem('access_code');
  },

  /**
   * Access Code 검증
   */
  validateAccessCode: async (code) => {
    try {
      const result = await fluxAPI.verifyAccessCode(code);
      if (result.valid) {
        accessCodeManager.setAccessCode(code);
      }
      return result.valid;
    } catch (error) {
      console.error('Access Code 검증 실패:', error);
      return false;
    }
  }
};

export default apiClient;